/**
 * Format helpers — the app uses DD-MM-YYYY everywhere.
 * Input can be a "YYYY-MM-DD" date string or a full ISO datetime string.
 *
 * All day-arithmetic helpers pin "today" to Australia/Sydney so results are
 * consistent regardless of the browser timezone (matches the server, which
 * also runs every date calc in Sydney).
 */

const pad = (n) => String(n).padStart(2, "0");

export const fmtDate = (val) => {
  if (!val) return "—";
  // Plain YYYY-MM-DD (avoid Date() timezone shift)
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split("-");
    return `${d}-${m}-${y}`;
  }
  const dt = new Date(val);
  if (Number.isNaN(dt.getTime())) return "—";
  return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()}`;
};

export const fmtDateTime = (val) => {
  if (!val) return "—";
  const dt = new Date(val);
  if (Number.isNaN(dt.getTime())) return "—";
  return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

// ---- Sydney-anchored day arithmetic ---------------------------------------

/** Today's calendar date in Sydney as a "YYYY-MM-DD" string. */
export const todayInSydney = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Sydney",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const d = parts.find((p) => p.type === "day").value;
  return `${y}-${m}-${d}`;
};

/** Whole-day delta from today-in-Sydney to the given YYYY-MM-DD (or ISO) date.
 *  Positive = future, 0 = today, negative = past. Returns null on bad input. */
export const daysUntil = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const iso = dateStr.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [ty, tm, td] = todayInSydney().split("-").map(Number);
  const [y, m, d] = iso.split("-").map(Number);
  const target = Date.UTC(y, m - 1, d);
  const today = Date.UTC(ty, tm - 1, td);
  return Math.round((target - today) / 86400000);
};

/** Next occurrence of a birthday (MM-DD from dobStr) as a "YYYY-MM-DD" string,
 *  measured in Sydney time. Returns null on bad input. */
export const nextBirthday = (dobStr) => {
  if (!dobStr || typeof dobStr !== "string") return null;
  const iso = dobStr.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [ty, tm, td] = todayInSydney().split("-").map(Number);
  const [, m, d] = iso.split("-").map(Number);
  let year = ty;
  const todayUtc = Date.UTC(ty, tm - 1, td);
  let nextUtc = Date.UTC(year, m - 1, d);
  if (nextUtc < todayUtc) {
    year += 1;
    nextUtc = Date.UTC(year, m - 1, d);
  }
  return `${year}-${pad(m)}-${pad(d)}`;
};
